import {useMemo} from 'react';
import {print} from 'graphql';
import {useQueries} from '@tanstack/react-query';

import {useService} from '@amblique/base/app/hooks/use-service';
import {ContentfulService} from '@amblique/contentful/app/services/contentful-service';
import {useHeaderBySiteId} from '@amblique/king-living/app/contentful/hooks/use-header';
import {useNavigationMenuById} from '@amblique/contentful/app/contentful/hooks/use-navigation-menu';
import {getMenuItemById} from '@amblique/contentful/app/contentful/queries';

/**
 * Custom hook to fetch Contentful header with nested data
 * Handles fetching Header, NavigationMenu, and Level 1 Menu Items separately
 * to avoid GraphQL query complexity errors
 *
 * @param {string} siteId - Site identifier (e.g., 'kl-au', 'kl-nz')
 * @param {Object} options - Hook options
 * @param {boolean} options.enabled - Whether the hook is enabled
 * @param {Function} options.onSuccess - Success callback
 * @param {Function} options.onError - Error callback
 * @returns {Object} Header data and loading/error states
 */
export function useContentfulHeader(siteId, options = {}) {
  const {
    enabled = true,
    onSuccess,
    onError,
  } = options;

  // Get ContentfulService for fetching menu items
  const contentfulService = useService(ContentfulService);

  // Step 1: Fetch Header by siteId with 'default' variant to avoid query complexity error
  // This gives us the Header with Base fragments for nested items (includes sys.id)
  const {
    data: headerBase,
    error: headerError,
    isLoading: isHeaderLoading,
    isFetching: isHeaderFetching,
  } = useHeaderBySiteId(siteId, {
    variant: 'default',
    queryOptions: {
      enabled,
      retry: false,
      onSuccess: (data) => {
        onSuccess?.(data);
      },
      onError: (error) => {
        onError?.(error);
      },
    },
  });

  // Step 2: Fetch NavigationMenu separately with its children data
  const navigationMenuId = headerBase?.mainNavigation?.sys?.id;
  const {
    data: navigationMenu,
    isLoading: isNavigationMenuLoading,
  } = useNavigationMenuById(navigationMenuId, {
    variant: 'default',
    queryOptions: {
      enabled: enabled && !!navigationMenuId,
      retry: false,
    },
  });

  // Step 3: Extract level 1 menu item IDs from navigationMenu
  const level1MenuItemIds = useMemo(() => {
    if (!navigationMenu?.menuItemsCollection?.items) return [];

    return navigationMenu.menuItemsCollection.items
      .map((item) => item?.sys?.id)
      .filter(Boolean);
  }, [navigationMenu]);

  // Step 4: Fetch all level 1 menu items separately to get their submenuCollection children
  // Use queryKey structure matching useMenuItemById to enable React Query deduplication
  const menuItemsQueries = useQueries({
    queries: level1MenuItemIds.map((menuItemId) => {
      const variables = {}; // Empty variables object to match useMenuItemById structure

      return {
        queryKey: [
          contentfulService?.key,
          'menuItem',
          'id',
          menuItemId,
          'default',
          contentfulService?.locale,
          JSON.stringify(variables) // Match useMenuItemById queryKey structure
        ],
        queryFn: async () => {
          if (!contentfulService) {
            throw new Error('ContentfulService not available');
          }

          const data = await contentfulService.query(
            print(getMenuItemById),
            {
              id: menuItemId,
              locale: contentfulService?.locale,
              preview: false,
              ...variables,
            }
          );

          return data.menuItem || null;
        },
        enabled: enabled && !!contentfulService && !!menuItemId,
        retry: false,
        staleTime: 10 * 60 * 1000, // 10 minutes
        gcTime: 15 * 60 * 1000, // 15 minutes
        refetchOnWindowFocus: false,
      };
    }),
  });

  // Extract menu items data from queries
  const menuItemsData = useMemo(() => {
    return menuItemsQueries
      .map((query) => query.data)
      .filter(Boolean);
  }, [menuItemsQueries]);

  const isMenuItemsLoading = menuItemsQueries.some((query) => query.isLoading);

  // Step 5: Merge NavigationMenu data with fetched level 1 menu items into header object
  const contentfulHeader = useMemo(() => {
    if (!headerBase) return null;
    if (!navigationMenu) return headerBase;

    // If we have menu items data, merge them
    let mergedNavigationMenu = navigationMenu;

    if (menuItemsData.length > 0 && navigationMenu?.menuItemsCollection?.items) {
      const menuItemsMap = new Map(menuItemsData.map((item) => [item?.sys?.id, item]));

      mergedNavigationMenu = {
        ...navigationMenu,
        menuItemsCollection: {
          ...navigationMenu.menuItemsCollection,
          items: navigationMenu.menuItemsCollection.items.map((item) => {
            const fetchedItem = menuItemsMap.get(item?.sys?.id);

            return fetchedItem || item;
          }),
        },
      };
    }

    return {
      ...headerBase,
      mainNavigation: mergedNavigationMenu,
    };
  }, [headerBase, navigationMenu, menuItemsData]);

  // Calculate overall loading state
  const isLoading = isHeaderLoading || isNavigationMenuLoading || isMenuItemsLoading;

  // Calculate if header is ready (has data and not loading)
  const isReady = enabled && !!contentfulHeader && !headerError && !isLoading;

  return {
    data: contentfulHeader,
    error: headerError,
    isLoading,
    isFetching: isHeaderFetching,
    isReady,
  };
}
